import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import type HotTable from '@handsontable//react';

import { _chunk, _isNil, _isNotEmpty, _set } from '@/littledash';

import type { Animal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import { toNumericId } from '@/helpers';
import type { CellChange, ChangeSource } from 'handsontable/common';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import { featuresSelector } from '@/support/Selectors';
import { ApiService } from '@/support/ApiService';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Spinner from '@/components/UI/Spinner';
import Switch from '@/components/UI/FormElements/Switch';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import { type AnimalIdentifierData, HoTcolumns } from './AssignIdentifiers.utils';
import BulkGenerateForm from './BulkGenerateForm';
import './AssignIdentifiers.scss';

/**
 * Lazy load both table versions to ensure complete isolation between Handsontable v12 and v16.
 * V12 and V16 register different cell types, validators, and editors globally in Handsontable.
 * With lazy loading, only the required version's modules are loaded based on the `hotUpgrade` flag.
 */
const AssignIdentifiersTableV12 = lazy(() => import('./AssignIdentifiersTableV12'));
const AssignIdentifiersTableV16 = lazy(() => import('./AssignIdentifiersTableV16'));

const CHUNK_SIZE = 50;

const assignIdentifiersSelector = createSelector([featuresSelector], (features) => ({
  assignIdentifiersTagUnique: features?.assign_identifiers_tag_unique ?? false,
  hotUpgrade: features?.hot_upgrade ?? false,
}));

interface AssignIdentifiersProps {
  animals: Array<Animal>;
  handleCallback: (message?: string) => void;
  closeModal: () => void;
}

const initialAnimalData = (animals: Array<Animal>): Array<AnimalIdentifierData> =>
  animals.map(({ id, name, cage, alt_ids: altIds }) => ({
    id,
    name: name ?? '',
    cage,
    alt_ids: { ...altIds },
  }));

const AssignIdentifiers: FC<AssignIdentifiersProps> = ({ animals, handleCallback, closeModal }) => {
  const { assignIdentifiersTagUnique, hotUpgrade } = useSelector(assignIdentifiersSelector);
  const [data, setData] = useState<Array<AnimalIdentifierData>>(initialAnimalData(animals));
  const [changes, setChanges] = useState<Record<ID, AnimalIdentifierData>>({});
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [reloadOnClose, setReloadOnClose] = useState(false);

  const [updatingAnimals, setUpdatingAnimals] = useState(false);
  const [requestError, setRequestError] = useState<Error | false>(false);

  const updateAnimals = async (animals: Array<AnimalIdentifierData>) =>
    ApiService.call({
      endpoint: 'PATCH /api/v1/animals/identifiers',
      body: {
        animals: animals.map(({ id, name, alt_ids }) => ({
          id: toNumericId(id),
          name,
          alt_ids: _isNotEmpty(alt_ids) ? alt_ids : null,
        })),
      },
      options: { onError: { throw: true, toast: false, slug: 'assign-identifiers-bulk-update' } },
    });

  const switchRef = useRef(false);
  const refV12 = useRef<HotTable>();
  const refV16 = useRef<HotTableRef>(null);

  const getHotInstance = () => {
    if (hotUpgrade) {
      return refV16.current?.hotInstance ?? null;
    }
    return refV12.current?.hotInstance ?? null;
  };

  const columns = useMemo(() => {
    if (assignIdentifiersTagUnique) {
      return HoTcolumns.filter((column) => column.data !== 'alt_ids.tag');
    }
    return HoTcolumns;
  }, [assignIdentifiersTagUnique]);

  const handleChangePayload = (pendingChanges: Record<ID, AnimalIdentifierData>) => Object.values(pendingChanges);

  const handleSave = async () => {
    getHotInstance()?.updateSettings({ readOnly: true });
    setUpdatingAnimals(true);
    setRequestError(false);
    try {
      await Promise.all(_chunk(handleChangePayload(changes), CHUNK_SIZE).map((batch) => updateAnimals(batch)));
      if (autoSaveEnabled) {
        getHotInstance()?.updateSettings({ readOnly: false });
      } else {
        handleCallback('Successfully assigned identifiers.');
      }
    } catch (error) {
      setRequestError(error as Error);
      getHotInstance()?.updateSettings({ readOnly: false });
    } finally {
      setUpdatingAnimals(false);
    }
  };

  const handleSubmit = async () => {
    if (!isInvalid) {
      return handleSave();
    }
    return Promise.reject(new Error('Table data invalid'));
  };

  const handleBeforeChange = (dataChanges: Array<CellChange> | null) => {
    if (_isNil(dataChanges)) {
      return;
    }
    const { updatedData, updatedChanges } = dataChanges.reduce<{
      updatedData: Array<AnimalIdentifierData>;
      updatedChanges: Record<ID, AnimalIdentifierData>;
    }>(
      ({ updatedData, updatedChanges }, [row, column, , newValue]) => {
        _set(updatedData, `[${row}].${column}`, newValue);
        const { id } = animals[row];
        const { name, alt_ids: altIds } = updatedChanges?.[id] ?? animals[row];
        const updatedAnimal: AnimalIdentifierData = {
          id,
          name: name ?? '',
          alt_ids: { ...altIds },
          cage: animals[row].cage,
        };
        _set(updatedAnimal, column as string, newValue);
        return {
          updatedData,
          updatedChanges: { ...updatedChanges, [id]: updatedAnimal },
        };
      },
      { updatedData: [...data], updatedChanges: { ...changes } }
    );
    setChanges(updatedChanges);
    setData(updatedData);
  };

  const handleAfterChange = (dataChanges: Array<CellChange> | null, source: ChangeSource | 'insert_row' | string) => {
    if (_isNil(dataChanges)) {
      return;
    }
    getHotInstance()?.validateRows(
      dataChanges.map((change) => change[0]),
      () => {
        if (source === 'edit' && !isInvalid && switchRef.current) {
          handleAutoSave(dataChanges);
        }
      }
    );
  };

  const handleAutoSave = (dataChanges: Array<CellChange>) => {
    const updates = dataChanges.map(([row, column, , newValue]) => {
      const { id } = animals[row];
      const { name, alt_ids: altIds } = changes[id] ?? animals[row];
      const updatedAnimal: AnimalIdentifierData = {
        id,
        name: name ?? '',
        alt_ids: { ...altIds },
        cage: animals[row].cage,
      };
      _set(updatedAnimal, column as string, newValue);
      return updatedAnimal;
    });

    if (_isNotEmpty(updates)) {
      updateAnimals(updates).catch((error) => {
        setRequestError(error as Error);
      });
    }
  };

  const handleBulkAssign = (dataChanges: Array<CellChange>) => {
    setAutoSaveEnabled(false);
    switchRef.current = false;
    setReloadOnClose(false);
    const rowPropChanges = dataChanges.map(
      ([row, prop, value]) => [row, prop, value] as [number, string | number, unknown]
    );
    getHotInstance()?.setDataAtRowProp(rowPropChanges);
  };

  const handleExitModal = () => {
    if (!updatingAnimals) {
      if (autoSaveEnabled || reloadOnClose) {
        handleCallback();
      }
      closeModal();
    }
  };

  useEffect(() => {
    if (autoSaveEnabled && _isNotEmpty(Object.keys(changes))) {
      handleSubmit().catch(() => {
        setAutoSaveEnabled(false);
        switchRef.current = false;
        setReloadOnClose(true);
        toast.warning('Disabled autosave as pre existing changes have errors');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveEnabled]);

  const handleSwitch = () => {
    switchRef.current = !autoSaveEnabled;
    setAutoSaveEnabled(!autoSaveEnabled);
  };

  return (
    <ModalContainer size="wide" className="bg-white flex flex-column justify-between items-start">
      <ModalHeader
        title="Assign Identifiers to animals"
        closeModal={handleExitModal}
        closeDisabled={updatingAnimals}
        className="pa3 bb b--moon-gray w-100"
      />
      <div className="pa3 w-100">
        {requestError !== false && <ApiErrorBanner error={requestError as Error} className="mb3" />}
        <BulkGenerateForm data={animals} setData={handleBulkAssign} />
        <div className="ow-spreadsheet-styles assign-identifier-spreadsheet">
          <Suspense fallback={<Loading />}>
            {hotUpgrade ? (
              <AssignIdentifiersTableV16
                data={data}
                columns={columns}
                innerRef={refV16}
                beforeChange={handleBeforeChange}
                afterChange={handleAfterChange}
                maxRows={data.length}
                setInvalid={setIsInvalid}
              />
            ) : (
              <AssignIdentifiersTableV12
                data={data}
                columns={columns}
                innerRef={refV12}
                beforeChange={handleBeforeChange}
                afterChange={handleAfterChange}
                maxRows={data.length}
                setInvalid={setIsInvalid}
              />
            )}
          </Suspense>
        </div>
      </div>
      <div className="pa3 bt b--moon-gray flex justify-between items-center w-100">
        <ModalActions
          onSubmit={handleSubmit}
          onCancel={handleExitModal}
          cancelBtnText="Close"
          submitButtonProps={{
            loading: updatingAnimals,
            disabled: updatingAnimals || autoSaveEnabled || isInvalid,
            tooltip: isInvalid ? spreadSheetValidationTooltip : undefined,
          }}
          cancelButtonProps={{ disabled: updatingAnimals }}
        />
        <div className="flex items-center">
          {autoSaveEnabled && updatingAnimals && (
            <div className="flex items-center mr3">
              <Spinner size="small" className="mr2" color="near-black" />
              <span className="f6 lh-title near-black">Saving</span>
            </div>
          )}
          <Switch value={autoSaveEnabled} onChange={handleSwitch} disabled={false} />
          <label className="ml2 mb0">Auto save</label>
        </div>
      </div>
    </ModalContainer>
  );
};

export default AssignIdentifiers;
